import { addDays, isAfter, isValid, parseISO } from "date-fns";

/**
 * The referral code a visitor arrived with, held across the signup flow.
 *
 * `?invitationCode=` survives a refresh (SignupScreen reads it from
 * `searchParams`) but not a navigation that drops the query string — a visitor
 * who clicks through to Terms and back, or bounces to Login and returns, would
 * otherwise land unattributed. Storage is the durable copy; the URL wins
 * whenever it is present.
 */
const PENDING_REFERRAL_CODE_KEY = "referralCode";

/**
 * How long a stored code stays usable.
 *
 * The window has to be bounded. Our students frequently sign up on facility
 * computer labs, library machines, and borrowed phones, and an unbounded code
 * would attribute every later stranger on that device to whoever visited first
 * — silently, since the leaked case has no query string to notice. A week
 * covers the real behaviour we want to keep ("I'll finish this tomorrow")
 * without leaving a shared machine harvesting indefinitely.
 */
const PENDING_REFERRAL_CODE_DAYS = 7;

type StoredReferralCode = {
  code: string;
  capturedAt: string;
};

export function clearPendingReferralCode(): void {
  try {
    window.localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
  } catch {
    // Safari private browsing and hardened privacy settings throw on access.
    // Attribution is best-effort; never let it break signup.
  }
}

function readRecord(): StoredReferralCode | null {
  const raw = window.localStorage.getItem(PENDING_REFERRAL_CODE_KEY);
  if (!raw) return null;

  const parsed = JSON.parse(raw);
  if (typeof parsed?.code !== "string" || !parsed.code) return null;
  if (typeof parsed?.capturedAt !== "string") return null;

  return parsed;
}

/**
 * Read the stored code. Returns `undefined` when absent, expired, unreadable,
 * or written by a build that predates the `capturedAt` stamp — every one of
 * those is treated as "no referral", and the entry is dropped so the next read
 * is cheap.
 */
export function getPendingReferralCode(): string | undefined {
  try {
    const record = readRecord();
    if (!record) {
      clearPendingReferralCode();
      return undefined;
    }

    const capturedAt = parseISO(record.capturedAt);
    if (
      !isValid(capturedAt) ||
      isAfter(new Date(), addDays(capturedAt, PENDING_REFERRAL_CODE_DAYS))
    ) {
      clearPendingReferralCode();
      return undefined;
    }

    return record.code;
  } catch {
    // Malformed JSON, or storage access denied. See clearPendingReferralCode.
    return undefined;
  }
}

export function setPendingReferralCode(code: string): void {
  try {
    const record: StoredReferralCode = {
      code,
      capturedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(
      PENDING_REFERRAL_CODE_KEY,
      JSON.stringify(record)
    );
  } catch {
    // See clearPendingReferralCode.
  }
}
